import { useNavigate } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import React, { useEffect } from 'react';
import {
  HeaderSection,
  AppTable,
  useFetchList,
  PAGE_SIZE_OPTIONS,
  IAppTableColumn,
  removeToken,
} from '@qlibs/react-components';
import { Actions, IAction } from '@qlibs/react-components/dist/Table';
import ListFilterRow from '../../components/ListFilterRow';
import { readListFilter, readListPage } from '../../helpers/listFilterQuery';
import { Alert, Button, Modal, Tag, message } from 'antd';
import { Grid, Table } from 'antd';
import {
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { RiEqualizer2Line } from '@remixicon/react';
import { GroupProperties } from './types/group.type';
import { httpRequest } from '../../helpers/api';
import { EDateFormat, SUPER_ADMIN_ROLE_ID } from '../../const/config';
import LinkTable from '../../components/LinkTable';
import { useAuthUser, useSignOut } from 'react-auth-kit';
import useGroup from '../../zustand/useGroup';
import {
  formatCurrentDate,
  EFormatCurrentDate,
} from '../../helpers/formatCurrentDate';
import { isRamus2GroupId } from '../../helpers/ramus2';
import { ColumnsType } from 'antd/lib/table';
import {
  GroupMobileFilterButton,
  GroupMobileFilterActions,
  GroupMobileFilterApplyButton,
  GroupMobileFilterDrawer,
  GroupMobileFilterField,
  GroupMobileFilterFields,
  GroupMobileFilterForm,
  GroupMobileFilterRow,
  GroupMobileFilterSelect,
  GroupMobileIconButton,
  GroupMobilePageButton,
  GroupMobilePagination,
  GroupMobilePaginationActions,
  GroupMobilePaginationInfo,
  GroupMobilePage,
  GroupMobileSearch,
  GroupMobileTableCard,
  MobileGroupHeader,
} from './mobile';
const moment = require('moment');

const FEATURE = 'GROUP';

const Group = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { isUserHasPermission } = usePermission();
  const auth = useAuthUser();
  const { group } = useGroup();

  const roleId = auth()?.role?.roleId;

  const handleCreateGroup = () => {
    navigate({ pathname: '/group/add' });
  };

  const [isModalDeleteOpen, setIsModalDeleteOpen] = React.useState(false);
  const [isModalRestoreOpen, setIsModalRestoreOpen] = React.useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = React.useState(false);
  const [mobileFilterValues, setMobileFilterValues] = React.useState({
    status: 'all',
    filterData: 'default',
  });
  const [currentData, setCurrentData] = React.useState<GroupProperties>();
  const [forceDeleteId, setForceDeleteId] = React.useState<string>();

  const {
    isLoading,
    data,
    pagination,
    query,
    setData,
    setQuery,
    changePage,
    changeLimit,
    handleSearch,
    pageQueries,
    filterDropdown,
  } = useFetchList<GroupProperties>({
    httpRequest: httpRequest as any,
    endpoint: 'group',
    limit: +PAGE_SIZE_OPTIONS[1],
    initialQuery: {
      filterStatus: readListFilter('filterStatus'),
      filterArchived: readListFilter('filterArchived'),
      search: readListFilter('search'),
    },
    pageQuery: {
      defaultValue: {
        page: readListPage(),
        filterStatus: '',
        filterArchived: '',
        search: '',
      },
    },
  });

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));
  const signOut = useSignOut();

  async function deleteData() {
    try {
      if (currentData) {
        const forceDelete =
          forceDeleteId && forceDeleteId === currentData.groupId;
        await httpRequest.delete('/group/' + currentData.groupId, {
          params: {
            forceDelete: forceDelete || undefined,
          },
        });
        message.success(
          `Success ${forceDelete ? 'delete' : 'archive'} ${currentData.name}`,
        );

        setQuery({
          limit: +PAGE_SIZE_OPTIONS[1],
        });

        if (group.groupId === currentData.groupId) {
          await delay(1000);
          // relogin
          await httpRequest.post('/auth/signout');
          signOut();
          removeToken();
          localStorage.removeItem('group-selected');
          localStorage.removeItem('map-settings');
          navigate('/login');
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalDeleteOpen(false);
      setCurrentData(undefined);
      setForceDeleteId(undefined);
    }
  }

  async function restoreData() {
    try {
      if (currentData) {
        await httpRequest.patch('/group/' + currentData.groupId + '/restore');
        message.success(`Success restore ${currentData.name}`);

        setQuery({
          limit: +PAGE_SIZE_OPTIONS[1],
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalRestoreOpen(false);
      setCurrentData(undefined);
      setForceDeleteId(undefined);
    }
  }

  async function handleDeleteGroup(id: any, forced?: boolean) {
    setIsModalDeleteOpen(true);
    const found = data.find((item) => item.groupId === id);
    if (found) {
      setCurrentData(found);
    }

    if (forced) {
      setForceDeleteId(id);
    }
  }

  async function handleRestoreGroup(id: any) {
    setIsModalRestoreOpen(true);
    const found = data.find((item) => item.groupId === id);
    if (found) {
      setCurrentData(found);
    }
  }

  function handleCancelDelete() {
    setIsModalDeleteOpen(false);
    setIsModalRestoreOpen(false);
    setCurrentData(undefined);
  }

  function isArchived(id?: string) {
    if (id) {
      const found = data.find((item) => item.groupId === id && item.deletedAt);
      if (found) {
        return true;
      }
    }

    return false;
  }

  const getGroupActions = (record: GroupProperties): IAction[] => {
    const isReadOnlyRamus2 = record.isRamus2 || isRamus2GroupId(record.groupId);

    return [
      isUserHasPermission([FEATURE + '.DETAIL'])
        ? {
            key: 'detail',
            label: 'Detail',
            onPress: (id?: string) => navigate('/group/' + id),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.UPDATE'])
        ? {
            key: 'update',
            label: 'Update',
            onPress: (id?: string) => navigate('/group/' + id + '/edit'),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.DELETE'])
        ? {
            key: 'archive',
            label: 'Delete',
            color: '#ff4d4f',
            disabled: (id?: string) => isArchived(id),
            disabledTooltip: 'This data is already archived',
            onPress: (id?: string) => handleDeleteGroup(id),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.RESTORE'])
        ? {
            key: 'restore',
            label: 'Restore',
            color: '#1890ff',
            disabled: (id?: string) => !isArchived(id),
            disabledTooltip: 'This data not in archived',
            onPress: (id?: string) => handleRestoreGroup(id),
          }
        : undefined,
      !isReadOnlyRamus2 && isUserHasPermission([FEATURE + '.FORCE_DELETE'])
        ? {
            key: 'delete',
            label: 'Force Delete',
            onPress: (id?: string) => handleDeleteGroup(id, true),
          }
        : undefined,
    ].filter(Boolean) as IAction[];
  };

  const columns: IAppTableColumn<GroupProperties>[] = [
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      type: 'detail',
      keyId: 'groupId',
      render: (_, records) => (
        <LinkTable onPress={() => navigate('/group/' + records.groupId)}>
          {records.deletedAt ? (
            <Tag color="#ff5500" className="mb-2">
              Archived
            </Tag>
          ) : (
            false
          )}
          ({records.nickname}) {records.name}
        </LinkTable>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (value, _) => (
        <Tag color={value === 'active' ? 'success' : 'error'}>
          {value.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'COMPANIES',
      dataIndex: 'companies',
      key: 'companies',
      render: (companies, _) => (companies ? companies.length : 0),
    },
    {
      title: 'CREATED AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'UPDATED AT',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'ACTION',
      key: 'action',
      render: (_, record) => {
        const actions = getGroupActions(record);

        if (!actions.length) {
          return null;
        }

        return <Actions id={record.groupId} actions={actions} />;
      },
    },
  ];

  const mobileColumns: ColumnsType<GroupProperties> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 217,
      render: (_, records) => (
        <LinkTable onPress={() => navigate('/group/' + records.groupId)}>
          {records.deletedAt ? (
            <Tag color="#ff5500" className="mb-2">
              Archived
            </Tag>
          ) : (
            false
          )}
          ({records.nickname}) {records.name}
        </LinkTable>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 86,
      render: (value) => (
        <Tag color={value === 'active' ? 'success' : 'error'}>
          {String(value || '').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Companies',
      dataIndex: 'companies',
      key: 'companies',
      width: 114,
      render: (companies) => (companies ? companies.length : 0),
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 153,
      render: (value) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'Update at',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 147,
      render: (value) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'Action',
      key: 'action',
      width: 122,
      render: (_, record) => {
        const actions = getGroupActions(record);

        if (!actions.length) {
          return null;
        }

        return <Actions id={record.groupId} actions={actions} />;
      },
    },
  ];

  const filterData: any = [
    {
      type: 'search',
      key: 'search',
      label: 'Search',
      placeholder: 'Search by group name',
      onChange: (value: any) => {
        handleSearch(value);
      },
      colSpan: isUserHasPermission([FEATURE + '.DELETE']) ? 12 : 18,
    },
    {
      type: 'select',
      key: 'status',
      label: 'Status',
      options: [
        {
          value: 'all',
          label: 'All',
        },
        {
          value: 'active',
          label: 'Active',
        },
        {
          value: 'inactive',
          label: 'Inactive',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterStatus: value === 'all' ? '' : value,
        });
      },
      colSpan: 6,
    },
    {
      type: 'select',
      key: 'filterData',
      label: 'Filter Data',
      options: [
        {
          value: 'default',
          label: 'Default',
        },
        {
          value: 'show',
          label: 'Include Archive Data',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterArchived: value === 'default' ? '' : value,
        });
      },
      colSpan: 6,
    },
  ].filter((item) =>
    !isUserHasPermission([FEATURE + '.DELETE'])
      ? item.key !== 'filterData'
      : item,
  );

  const mobilePagination = {
    current: Number((pagination as any)?.page || pageQueries.page || 1),
    pageSize: Number(
      (pagination as any)?.limit || query.limit || +PAGE_SIZE_OPTIONS[1],
    ),
    total: Number((pagination as any)?.total || data.length),
    showSizeChanger: false,
    showTotal: (total: number, range: [number, number]) =>
      `Showing ${range[0]} to ${range[1]} of ${total} results`,
    onChange: (page: number, pageSize: number) => changePage(page, pageSize),
  };
  const mobileCurrentPage = mobilePagination.current;
  const mobilePageSize = mobilePagination.pageSize;
  const mobileTotal = mobilePagination.total;
  const mobileTotalPages = Math.max(1, Math.ceil(mobileTotal / mobilePageSize));
  const mobileStartResult =
    mobileTotal === 0 ? 0 : (mobileCurrentPage - 1) * mobilePageSize + 1;
  const mobileEndResult = Math.min(
    mobileCurrentPage * mobilePageSize,
    mobileTotal,
  );

  const handleMobilePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > mobileTotalPages) {
      return;
    }

    changePage(nextPage, mobilePageSize);
  };

  React.useEffect(() => {
    if (!isMobileFilterOpen) {
      return;
    }

    setMobileFilterValues({
      status:
        (pageQueries as any).status ||
        (pageQueries as any).filterStatus ||
        'all',
      filterData:
        (pageQueries as any).filterData ||
        (pageQueries as any).filterArchived ||
        'default',
    });
  }, [isMobileFilterOpen, pageQueries]);

  const handleApplyMobileFilter = () => {
    filterDropdown({
      filterStatus:
        mobileFilterValues.status === 'all' ? '' : mobileFilterValues.status,
      filterArchived:
        mobileFilterValues.filterData === 'default'
          ? ''
          : mobileFilterValues.filterData,
    });
    setIsMobileFilterOpen(false);
  };

  return (
    <React.Fragment>
      {isMobile ? (
        <GroupMobilePage>
          <MobileGroupHeader
            title="Group"
            onBack={() => navigate(-1)}
            action={
              isUserHasPermission([FEATURE + '.CREATE']) ? (
                <GroupMobileIconButton
                  type="button"
                  aria-label="Add group"
                  onClick={handleCreateGroup}
                >
                  <PlusOutlined />
                </GroupMobileIconButton>
              ) : null
            }
          />

          <GroupMobileFilterRow>
            <GroupMobileSearch
              allowClear
              defaultValue={pageQueries.search}
              placeholder="Search by group name"
              suffix={<SearchOutlined style={{ color: '#9facbf' }} />}
              onChange={(event) => handleSearch(event.target.value)}
            />
            <GroupMobileFilterButton
              type="button"
              aria-label="Open group filters"
              onClick={() => setIsMobileFilterOpen(true)}
            >
              <RiEqualizer2Line size={20} />
            </GroupMobileFilterButton>
          </GroupMobileFilterRow>

          <GroupMobileTableCard>
            <Table<GroupProperties>
              loading={isLoading}
              rowKey="groupId"
              columns={mobileColumns}
              dataSource={data}
              size="small"
              scroll={{ x: 839 }}
              pagination={false}
            />
          </GroupMobileTableCard>

          <GroupMobilePagination>
            <GroupMobilePaginationInfo>
              Showing {mobileStartResult} to {mobileEndResult} of {mobileTotal}{' '}
              results
            </GroupMobilePaginationInfo>
            <GroupMobilePaginationActions>
              <GroupMobilePageButton
                type="button"
                disabled={mobileCurrentPage <= 1}
                onClick={() => handleMobilePageChange(mobileCurrentPage - 1)}
                aria-label="Previous page"
              >
                <LeftOutlined />
              </GroupMobilePageButton>
              <GroupMobilePageButton type="button" $active>
                {mobileCurrentPage}
              </GroupMobilePageButton>
              {mobileCurrentPage < mobileTotalPages ? (
                <GroupMobilePageButton
                  type="button"
                  onClick={() => handleMobilePageChange(mobileCurrentPage + 1)}
                >
                  {mobileCurrentPage + 1}
                </GroupMobilePageButton>
              ) : null}
              <GroupMobilePageButton
                type="button"
                disabled={mobileCurrentPage >= mobileTotalPages}
                onClick={() => handleMobilePageChange(mobileCurrentPage + 1)}
                aria-label="Next page"
              >
                <RightOutlined />
              </GroupMobilePageButton>
            </GroupMobilePaginationActions>
          </GroupMobilePagination>
        </GroupMobilePage>
      ) : (
        <>
          <HeaderSection
            title={'Group'}
            rightAction={[
              isUserHasPermission([FEATURE + '.CREATE']) ? (
                <Button type="primary" onClick={handleCreateGroup}>
                  Add Group
                </Button>
              ) : (
                false
              ),
            ]}
            subtitle="Manage data group that consist of multiple companies"
          />

          <ListFilterRow
            filterValues={{
              search: pageQueries.search,
              status: pageQueries.filterStatus || 'all',
              filterData: pageQueries.filterArchived || 'default',
            }}
            filters={[filterData]}
          />

          <AppTable
            isLoading={isLoading}
            keyId="groupId"
            columns={columns}
            data={data}
            pagination={pagination}
            onChangePage={changePage}
            onChangeLimit={changeLimit}
            // _table={{
            //   scroll:{ x: '100%' }
            // } as any}
          />
        </>
      )}

      <Modal
        title={
          forceDeleteId ? 'Force Delete Confirmation' : 'Delete Confirmation'
        }
        open={isModalDeleteOpen}
        onOk={deleteData}
        okButtonProps={{
          style: {
            background: 'red',
          },
        }}
        onCancel={handleCancelDelete}
      >
        {currentData && currentData?.groupId === group?.groupId ? (
          <Alert
            style={{ marginBottom: 16 }}
            message="Warning"
            description="Your are try to remove active group. After delete process you need to re-login"
            type="warning"
            showIcon
          />
        ) : (
          false
        )}
        {forceDeleteId ? (
          <p>
            Are you sure want to permanently delete this data? This data will be
            deleted and only can't be undo.
          </p>
        ) : (
          <p>
            Are you sure want to delete this data? This data will be archived
            and only User with restore permission can undo this action.
          </p>
        )}
      </Modal>

      <Modal
        title="Restore Confirmation"
        open={isModalRestoreOpen}
        onOk={restoreData}
        okButtonProps={{
          style: {
            background: 'blue',
          },
        }}
        onCancel={handleCancelDelete}
      >
        <p>Are you sure want to restore this data?.</p>
      </Modal>

      <GroupMobileFilterDrawer
        title="Filter"
        placement="bottom"
        height="auto"
        open={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
      >
        <GroupMobileFilterForm>
          <GroupMobileFilterFields>
            <GroupMobileFilterField>
              Status
              <GroupMobileFilterSelect
                value={mobileFilterValues.status}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
                onChange={(value) =>
                  setMobileFilterValues((prev) => ({
                    ...prev,
                    status: String(value),
                  }))
                }
              />
            </GroupMobileFilterField>
            {isUserHasPermission([FEATURE + '.DELETE']) ? (
              <GroupMobileFilterField>
                Filter Data
                <GroupMobileFilterSelect
                  value={mobileFilterValues.filterData}
                  options={[
                    { value: 'default', label: 'All' },
                    { value: 'show', label: 'Include Archive Data' },
                  ]}
                  onChange={(value) =>
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      filterData: String(value),
                    }))
                  }
                />
              </GroupMobileFilterField>
            ) : null}
          </GroupMobileFilterFields>
          <GroupMobileFilterActions>
            <GroupMobileFilterApplyButton
              type="primary"
              onClick={handleApplyMobileFilter}
            >
              Apply
            </GroupMobileFilterApplyButton>
          </GroupMobileFilterActions>
        </GroupMobileFilterForm>
      </GroupMobileFilterDrawer>
    </React.Fragment>
  );
};
export default Group;
